import { DateTime } from 'luxon';
import { pickBy, isArray } from 'lodash';

import { Model } from '@app/models/model';
import { config } from '@config/config';

export type Bucket = 'MIN' | 'HR' | 'DAY' | 'MON' | 'YR';
export type Operation = 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'CNT' | undefined;
export type QueryUnit = 'GALLONS' | 'CUBIC_FEET' | 'CUBIC_METERS' | 'LITERS';

export interface QueryInput {
    device_id?: string;
    since_datetime?: string | DateTime;
    until_datetime?: string | DateTime;
    bucket?: Bucket;
    group_multiplier?: number;
    operation?: Operation;
    units?: QueryUnit;
    data?: Array<{
        value: number;
        datetime?: string;
    }>;
    value?: number;
    types?: string[];
    previous_period?: string;
}

export class Query extends Model {
    /**
     * This is the request id sent to the backend, and it is
     * the request fields joined by the | character, omitting nulls.
     * The queryId is the id without the device_id
     */
    id: string;
    queryId: string;
    device_id: string;
    since_datetime: string;
    until_datetime: string;
    bucket: Bucket;
    group_multiplier: number;
    operation?: Operation;
    units?: QueryUnit;

    data: Array<{
        value: number;
        datetime?: string;
        predicted?: boolean;
        types?: {
            outdoor: number;
        };
        previous?: any;
    }>;
    value: number;
    types?: string[];
    previous_period?: string;
    // front end
    detailPlus?: any;

    constructor(input?: QueryInput) {
        super({});
        if (input) {
            this.device_id = input.device_id;
            this.since_datetime =
                typeof input.since_datetime === 'string'
                    ? input.since_datetime
                    : input.since_datetime.toFormat(config.datetimeFormats.QUERY);
            this.until_datetime =
                typeof input.until_datetime === 'string'
                    ? input.until_datetime
                    : input.until_datetime.toFormat(config.datetimeFormats.QUERY);
            this.bucket = input.bucket;
            this.group_multiplier = input.group_multiplier || 1;
            this.operation = input.operation;
            this.units = input.units;
            this.data = input.data;
            this.value = input.value;
            this.types = input.types;
            this.previous_period = input.previous_period;

            // Fix for really long usage alerts with too many data points
            if (
                this.bucket === 'MIN' &&
                DateTime.fromSQL(this.until_datetime).diff(DateTime.fromSQL(this.since_datetime), 'hours').hours > 16
            ) {
                this.until_datetime = DateTime.fromSQL(this.since_datetime)
                    .plus({ hours: 16 })
                    .toFormat(config.datetimeFormats.QUERY);
            }
            this.updateID();
        }
    }

    updateID() {
        const arr = [
            this.since_datetime ? this.since_datetime : '',
            this.until_datetime ? this.until_datetime : '',
            this.bucket,
            this.group_multiplier,
            this.operation,
            this.units,
            this.types,
            this.previous_period,
        ].filter((val) => !!val);

        this.queryId = arr.join('|');

        arr.unshift(this.device_id);
        this.id = arr.join('|');
    }

    setData(input: Array<any> | Record<string, any[]> | { value: number }) {
        if (isArray(input) && !input[0].datetime) {
            this.value = input[0].value;
        } else if (isArray(input)) {
            this.data = input;
        } else {
            this.value = input.value as number;
        }
    }

    toRequestBody() {
        return pickBy(
            {
                request_id: this.id,
                since_datetime: this.since_datetime,
                until_datetime: this.until_datetime,
                bucket: this.bucket,
                group_multiplier: this.group_multiplier,
                operation: this.operation,
                units: this.units,
                types: this.types,
                previous_period: this.previous_period,
            },
            (val) => !!val
        );
    }
}
